import { ClientInfo } from "../models/Client/ClientInfo";
import useApi from "../services/ApiService";
import {getClientStatus} from "../models/Client/ClientStatus";
import { extractApiErrorMessage } from "../lib/apiError";
import {ClientInstallationStatus} from "../models/Client/ClientInstallationStatus";

export class ClientInfoRepository {
  private api = useApi();

  async deleteClient(clientId: string): Promise<void> {
    try {
      return await this.api.delete(`/api/Clients/${clientId}`);
    } catch (error) {
      throw new Error("Failed to delete client");
    }
  }

  async forceRemoveClient(clientId: string): Promise<void> {
    try {
      return await this.api.delete(`/api/Clients/${clientId}/force-remove`);
    } catch (error) {
      throw new Error("Failed to force remove client");
    }
  }

  /** Takes a Pending Uninstall back before the updater acts on it. */
  async cancelUninstall(clientId: string): Promise<void> {
    try {
      await this.api.patch("/api/Clients/PatchClientInstallationStatus", {
        clientId,
        clientInstallationStatus: ClientInstallationStatus.Installed,
      });
    } catch (error) {
      throw new Error(extractApiErrorMessage(error, "Failed to cancel the uninstall"));
    }
  }

  async uninstallClient(clientId: string, reason?: string): Promise<void> {
    const patchClientRequest = {
      clientId: clientId,
      clientInstallationStatus: ClientInstallationStatus.PendingUninstall,
      reason: reason
    };
    try {
      return await this.api.patch("/api/Clients/PatchClientInstallationStatus", patchClientRequest);
    } catch (error) {
      throw new Error("Failed to set client uninstall status");
    }
  }

  async setActiveConfig(clientId: string, configId: string | null, reason?: string): Promise<void> {
    const patchClientRequest = {
      clientId: clientId,
      clientConfigurationId: configId,
      reason: reason
    };
    try {
      return await this.api.patch("/api/Clients/PatchClientPinnedConfiguration", patchClientRequest);
    } catch (error) {
      throw new Error("Failed to patch client active configuration");
    }
  }
  
    async resetFailedInstallation(organizationId: string, clientId: string, version: string, applicationType: string): Promise<void> {
        const resetFailedInstallationRequest = {
            OrganizationId: organizationId,
            ClientId: clientId,
            Version: version,
            ApplicationType: applicationType
    };
    try {
        await this.api.post("api/updates/versions/reset-failed-installations", resetFailedInstallationRequest);
    } catch (error) {
      throw new Error("Failed to reset failed installation count");
    }
  }

  async patchClientCacheSettings(clientId: string, useCache: boolean, useDeltas: boolean, rebuildCache: boolean, reason?: string): Promise<void> {
    try {
      await this.api.patch("/api/Clients/PatchClientCacheSettings", {
        clientId,
        useCache,
        useDeltas,
        rebuildCache,
        reason,
      });
    } catch (error) {
      throw new Error("Failed to patch client cache settings");
    }
  }

  async getClientInfos(organizationId: string, configurationId?: string): Promise<ClientInfo[]> {
    try {
      const response = await this.api.get<ClientInfo[]>(
        `/api/Clients?OrganizationId=${organizationId}`,
        {
          params: {
            FilterSoftDeleted: true,
            ...(configurationId && { ConfigurationId: configurationId }),
          },
        }
      );

      return response.data.map(client => ({
        ...client,
        status: getClientStatus(client.isActive, client.installationStatus),
      }));
    } catch (error) {
      throw new Error("Failed to fetch client info");
    }
  }
}

export default ClientInfoRepository;
